/**
 * Документы и согласия.
 *
 * Здесь приложение говорит о себе то, что можно сказать честно, и молчит там,
 * где сказать нечего. Итоговые тексты — условия использования и политика
 * обработки данных — готовятся отдельно, с юристом; выдавать написанное
 * разработчиком за согласованный документ нельзя, а подставлять ссылки-заглушки
 * и называть это готовым — тем более.
 *
 * Поэтому экран состоит из двух частей:
 *
 * · **что приложение делает с данными** — проверяемые утверждения о работе
 *   программы, за которые отвечает код, а не юрист;
 * · **что подтвердил человек** — записи согласий с версией, временем и
 *   способом, включая ещё не данные.
 *
 * Пределы прогноза и ИИ названы здесь же. Обещать, что расчёт сбудется, или
 * что за его последствия никто не отвечает, одинаково нечестно.
 */
import {
  REQUIRED_CONSENTS, type Consent, type ConsentKind,
} from '@skoka/core';
import { SheetWrap } from './sheet.js';
import { Tip } from './tip.js';
import { recordConsent, type AppState } from './store.js';
import { dayMonthYear } from './text.js';
import { Icon } from './icon.js';

/**
 * Редакция документов.
 *
 * Документы опубликованы 22.10.2026 на skokadeneg.ru/legal (docs/legal →
 * apps/site). Меняется вместе с текстом и записывается в каждое новое согласие.
 * Новая редакция заново не спрашивается: продолжая пользоваться, человек её
 * принимает (условия, п. 8.3; решение владельца 22.10.2026), а данные раньше
 * согласия остаются в силе — см. currentConsent.
 */
export const DOC_VERSION = '1.3';

/** Опубликованные документы: адреса на сайте (apps/site/legal.mjs). */
export const LEGAL_URL = {
  terms: 'https://skokadeneg.ru/legal/terms/',
  privacy: 'https://skokadeneg.ru/legal/privacy/',
  offer: 'https://skokadeneg.ru/legal/offer/',
  autorenewal: 'https://skokadeneg.ru/legal/autorenewal/',
  cookies: 'https://skokadeneg.ru/legal/cookies/',
  marketing: 'https://skokadeneg.ru/legal/marketing/',
  ads: 'https://skokadeneg.ru/legal/ads/',
} as const;

/**
 * Действующее согласие вида — последняя запись, в какой бы редакции её ни дали.
 * Новое согласие отдельно спрашивается только на новую цель обработки.
 */
export function currentConsent(consents: readonly Consent[], kind: ConsentKind) {
  const last = consents.filter((c) => c.kind === kind)
    .sort((a, b) => (a.acceptedAt < b.acceptedAt ? -1 : 1)).at(-1);
  return last
    ? { granted: last.granted, at: last.acceptedAt, method: last.method, version: last.documentVersion }
    : { granted: false, at: null, method: null, version: null };
}

const DOCS: { title: string; url: string }[] = [
  { title: 'Пользовательское соглашение', url: LEGAL_URL.terms },
  { title: 'Политика обработки персональных данных', url: LEGAL_URL.privacy },
  { title: 'Оферта SKOKA Pro', url: LEGAL_URL.offer },
  { title: 'Согласие на автопродление', url: LEGAL_URL.autorenewal },
  { title: 'Cookies и хранение в браузере', url: LEGAL_URL.cookies },
  { title: 'Согласие на обработку данных для рассылки', url: LEGAL_URL.marketing },
  { title: 'Согласие на получение рекламы', url: LEGAL_URL.ads },
];

export const CONSENT_LABEL: Record<ConsentKind, string> = {
  terms: 'Условия использования',
  privacy: 'Обработка персональных данных',
  ai_processing: 'Передача данных в модель для разбора',
  marketing: 'Рекламные сообщения',
};

const CONSENT_WHY: Record<ConsentKind, string> = {
  terms: 'Как устроено пользование приложением и что оно обещает.',
  privacy: 'Какие данные обрабатываются, зачем и сколько хранятся.',
  ai_processing: 'Без него разбор невозможен: данные уходят в модель.',
  marketing: 'Необязательно. Без него всё работает так же.',
};

/** Что программа делает на самом деле — это проверяется кодом. */
const FACTS: { title: string; text: string }[] = [
  {
    title: 'Расчёты идут на устройстве',
    text: 'Остаток, планы, прогнозы и распределение считаются в браузере. '
      + 'Без сети приложение продолжает работать.',
  },
  {
    title: 'Аккаунт — это перенос между устройствами',
    text: 'После входа снимок данных отправляется на сервер, чтобы открыть '
      + 'ту же картину с другого устройства. Без аккаунта данные хранятся только '
      + 'в браузере — на сервер уходит лишь то, что ты сам отправляешь на разбор '
      + 'или распознавание.',
  },
  {
    title: 'Разбор и распознавание уходят в модель',
    text: 'Для разбора картина денег отправляется на сервер и в языковую модель '
      + 'Yandex Cloud; для записи фразой, голосом, по чеку или выписке — сама фраза, '
      + 'запись или снимок. На сервере это не сохраняется. Без разбора и '
      + 'распознавания приложение работает.',
  },
  {
    title: 'Удаление уносит данные с сервера и с устройства',
    text: 'Удалить аккаунт можно из профиля в любой момент: ждать конца '
      + 'оплаченного периода не нужно, будущие списания прекращаются тем же '
      + 'действием. Остаются запись о том, что удаление было, — когда и с хешем '
      + 'почты, — и записи об оплатах без привязки к аккаунту: их хранить обязывает '
      + 'закон. Из резервных копий сервера данные уходят вместе с истечением '
      + 'копии, до восьми недель.',
  },
  {
    title: 'Чеки хранятся на устройстве',
    text: 'Снимок чека хранится в браузере и в синхронизацию не попадает. Для '
      + 'распознавания он уходит на сервер и не сохраняется там; остаются только '
      + 'реквизиты документа — чтобы один чек не стал двумя покупками.',
  },
];

/** Пределы: то, чего расчёт и модель не могут, названо прямо. */
const LIMITS: string[] = [
  'Прогноз — это расчёт по названным данным, а не обещание. Доход может '
  + 'прийти позже или меньше, расходы — оказаться другими.',
  'Разбор делает языковая модель. Она ошибается, и её ответ — не '
  + 'индивидуальная финансовая, юридическая или налоговая рекомендация.',
  'Решение всегда за тобой. Приложение не распоряжается деньгами и не '
  + 'совершает платежей.',
  'Ответственность за ущерб приложение с себя не снимает: обещать обратное '
  + 'было бы неправдой независимо от того, что написано мелким шрифтом.',
];

export function Documents({ state, onClose, onSaved }: {
  state: AppState; onClose: () => void; onSaved: () => void;
}) {
  const kinds: ConsentKind[] = ['terms', 'privacy', 'ai_processing', 'marketing'];

  return (
    <SheetWrap title="Условия, данные и согласия" onClose={onClose}>
      <div className="form">
        {/*
          Главное — сказано первым. Человек, который открыл этот экран перед
          регистрацией, должен узнать положение дел, а не искать его между
          абзацами.
        */}
        <section className="blk">
          <div className="blkhead"><b>Документы</b>
            <span className="tag soft">редакция {DOC_VERSION}</span></div>
          {DOCS.map((d) => (
            <a className="li" key={d.url} href={d.url} target="_blank" rel="noopener">
              <div className="nm"><b>{d.title}</b><span>на сайте skokadeneg.ru</span></div>
              <span className="ch"><Icon name="chevron-right" size={13} /></span>
            </a>
          ))}
        </section>

        <section className="blk">
          <div className="blkhead"><b>Что происходит с данными</b></div>
          {FACTS.map((f) => (
            <div className="li" key={f.title}>
              <div className="nm"><b>{f.title}</b><span>{f.text}</span></div>
            </div>
          ))}
        </section>

        <section className="blk">
          <div className="blkhead"><b>Пределы прогноза и ИИ</b></div>
          {LIMITS.map((l) => (
            <div className="li" key={l.slice(0, 24)}><div className="nm"><span>{l}</span></div></div>
          ))}
        </section>

        <section className="blk">
          <div className="blkhead"><b>Твои согласия
            <Tip>Отзыв не стирает прошлое согласие: он записывается отдельным
              событием, и обе даты остаются видны.</Tip></b></div>
          {kinds.map((kind) => {
            const st = currentConsent(state.consents, kind);
            const required = REQUIRED_CONSENTS.includes(kind);
            return (
              <div className="li" key={kind}>
                <div className="nm">
                  <b>{CONSENT_LABEL[kind]}{required ? '' : ' · необязательно'}</b>
                  <span>
                    {st.granted && st.at
                      ? `дано ${dayMonthYear(st.at.slice(0, 10))} · редакция ${st.version} · ${
                        st.method === 'checkbox' ? 'отметкой' : 'кнопкой'}`
                      : CONSENT_WHY[kind]}
                  </span>
                </div>
                <button className="btn inline"
                  onClick={async () => {
                    await recordConsent({
                      kind, documentVersion: DOC_VERSION, method: 'button',
                      granted: !st.granted,
                    });
                    onSaved();
                  }}>
                  {st.granted ? 'Отозвать' : 'Дать согласие'}
                </button>
              </div>
            );
          })}
        </section>
      </div>
    </SheetWrap>
  );
}
